import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { DownloadIcon, PlusIcon, RefreshCwIcon } from 'lucide-react';
import { EmptyStateTitle } from '@/shared/ui';
import { useTranslation } from 'react-i18next';

interface DeliveryJobEmptyStateProps {
  /** True when a search term or filter is active and matched nothing. */
  hasActiveFilters?: boolean;
  /** True when the request failed, which is not the same as the project having no jobs. */
  hasLoadFailed?: boolean;
  onNewJob?: () => void;
  onClearFilters?: () => void;
  onRetry?: () => void;
  /**
   * The toolbar is not on screen in this state, so the template it normally
   * offers is only reachable from here — and this is where someone with no jobs
   * yet actually needs it.
   */
  onDownloadTemplate?: () => void;
  isDownloadingTemplate?: boolean;
}

/**
 * Empty state for the jobs grid, matching the campaigns and projects lists:
 * iconless, vertically centered, with a create CTA only on the initial empty
 * list — starting a job does not clear a filter.
 */
export function DeliveryJobEmptyState({
  hasActiveFilters = false,
  hasLoadFailed = false,
  onNewJob,
  onClearFilters,
  onRetry,
  onDownloadTemplate,
  isDownloadingTemplate = false,
}: DeliveryJobEmptyStateProps = {}) {
  const { t } = useTranslation('projects');

  // Checked before the filter case, and before the initial one: a list that failed to load
  // is not a list with nothing in it. Rendering "upload a media plan" over a failed request
  // tells someone their jobs are gone, and offers them a second one as the remedy.
  if (hasLoadFailed) {
    return (
      <Empty role="alert" data-testid="delivery-job-load-error" className="flex-1">
        <EmptyHeader className="w-full">
          <EmptyStateTitle>
            {t('deliveryJobGrid.deliveryJobEmptyState.couldNotLoadJobs')}
          </EmptyStateTitle>
          <EmptyDescription className="w-full max-w-[36rem] text-balance">
            {t('deliveryJobGrid.deliveryJobEmptyState.jobsAreStillThere')}
          </EmptyDescription>
        </EmptyHeader>
        {onRetry ? (
          <EmptyContent>
            <Button variant="outline" onClick={onRetry}>
              <RefreshCwIcon className="size-md" />
              {t('deliveryJobGrid.deliveryJobEmptyState.tryAgain')}
            </Button>
          </EmptyContent>
        ) : null}
      </Empty>
    );
  }

  if (hasActiveFilters) {
    return (
      <Empty role="status" data-testid="delivery-job-empty-state" className="flex-1">
        <EmptyHeader>
          <EmptyStateTitle>
            {t('deliveryJobGrid.deliveryJobEmptyState.noMatchingJobs')}
          </EmptyStateTitle>
          <EmptyDescription>
            {t('deliveryJobGrid.deliveryJobEmptyState.tryADifferentSearch')}
          </EmptyDescription>
        </EmptyHeader>
        {onClearFilters ? (
          <EmptyContent>
            <Button variant="outline" onClick={onClearFilters}>
              {t('deliveryJobGrid.deliveryJobEmptyState.clearFilters')}
            </Button>
          </EmptyContent>
        ) : null}
      </Empty>
    );
  }

  return (
    <Empty role="status" data-testid="delivery-job-empty-state" className="flex-1">
      {/* `w-full` so the description below has a real width to cap against: EmptyHeader centres
          its children, so by default it takes its width from them and a percentage on the
          description would resolve against that element's own content. */}
      <EmptyHeader className="w-full">
        <EmptyStateTitle>{t('deliveryJobGrid.deliveryJobEmptyState.jobs')}</EmptyStateTitle>
        {/* Unconstrained this sentence needs ~950px and runs as one line on a wide viewport, so
            it is held to a width that breaks it in two, evenly. */}
        <EmptyDescription className="w-full max-w-[36rem] text-balance">
          {t('deliveryJobGrid.deliveryJobEmptyState.uploadAMediaPlan')}
        </EmptyDescription>
      </EmptyHeader>
      {onNewJob || onDownloadTemplate ? (
        <EmptyContent>
          <div className="flex items-center gap-xs">
            {onDownloadTemplate ? (
              <Button
                variant="outline"
                onClick={onDownloadTemplate}
                disabled={isDownloadingTemplate}
              >
                <DownloadIcon className="size-md" />
                {isDownloadingTemplate
                  ? t('deliveryJobGrid.deliveryJobEmptyState.preparing')
                  : t('deliveryJobGrid.deliveryJobEmptyState.template')}
              </Button>
            ) : null}
            {onNewJob ? (
              <Button onClick={onNewJob}>
                <PlusIcon className="size-md" />
                {t('deliveryJobGrid.deliveryJobEmptyState.newJob')}
              </Button>
            ) : null}
          </div>
        </EmptyContent>
      ) : null}
    </Empty>
  );
}
